import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'

type PrimaryButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode
  variant?: 'orange' | 'dark' | 'outline' | 'gray'
  size?: 'sm' | 'md' | 'lg'
}

const variantClasses = {
  orange: 'bg-brand-orange text-white hover:bg-[#e5631f]',
  dark: 'bg-[#030303] text-white hover:bg-black',
  outline: 'border border-[#3570E98E] bg-white text-brand-blue',
  gray: 'border border-[#3570E98E] bg-[#D3D3D3] text-black',
}

const sizeClasses = {
  sm: 'h-[42px] rounded-[14px] px-5 text-sm font-semibold',
  md: 'h-[58px] rounded-[37px] px-5 text-base font-medium',
  lg: 'h-[89px] rounded-[13px] px-5 text-xl font-semibold',
}

export default function PrimaryButton({
  children,
  variant = 'orange',
  size = 'md',
  className = '',
  ...props
}: PrimaryButtonProps) {
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center font-poppins transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98] ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  )
}

type ArrowCircleButtonProps = {
  direction?: 'right' | 'left'
  onClick?: () => void
  to?: string
  className?: string
  dark?: boolean
  'aria-label'?: string
}

export function ArrowCircleButton({
  direction = 'right',
  onClick,
  to,
  className = '',
  dark = true,
  'aria-label': ariaLabel,
}: ArrowCircleButtonProps) {
  const classes = `flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-full border border-[#C8C8C8] transition-all duration-200 hover:-translate-y-0.5 hover:opacity-90 active:scale-[0.96] ${
    dark
      ? 'bg-[#211F1F] hover:bg-[#2f2d2d]'
      : 'bg-surface-gray hover:bg-[#e8e8e8]'
  } ${className}`

  const icon = (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={direction === 'left' ? 'rotate-180' : ''}
    >
      <path
        d="M7 17L17 7"
        stroke={dark ? '#FFFFFF' : '#FF7027'}
        strokeWidth="2.35"
        strokeLinecap="square"
      />
      <path
        d="M8 7H17V16"
        stroke={dark ? '#FFFFFF' : '#FF7027'}
        strokeWidth="2.35"
        strokeLinecap="square"
        strokeLinejoin="miter"
      />
    </svg>
  )

  if (to) {
    return (
      <Link
        to={to}
        aria-label={
          ariaLabel || (direction === 'right' ? 'Go to page' : 'Previous')
        }
        className={classes}
      >
        {icon}
      </Link>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={
        ariaLabel || (direction === 'right' ? 'Next' : 'Previous')
      }
      className={classes}
    >
      {icon}
    </button>
  )
}
